import { useState, ReactNode } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Check, Copy, Link } from 'lucide-react'
import { cn } from '@/lib/utils'

// Slugify text for anchor links
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
}

// Code Block Component
interface CodeBlockProps {
  children: string
  language?: string
  className?: string
}

export function CodeBlock({ children, language, className }: CodeBlockProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(children)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Detect language from className (e.g., "language-typescript")
  const detectedLang = className?.replace('language-', '') || language || 'text'

  return (
    <div className="group relative my-6 rounded-lg overflow-hidden border border-border bg-[#1e1e1e]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-border/50">
        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">
          {detectedLang}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code */}
      <SyntaxHighlighter
        language={detectedLang}
        style={oneDark}
        customStyle={{
          margin: 0,
          padding: '1rem',
          background: 'transparent',
          fontSize: '13px',
          lineHeight: '1.6',
        }}
        codeTagProps={{
          style: {
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
          }
        }}
      >
        {children.trim()}
      </SyntaxHighlighter>
    </div>
  )
}

// Inline Code Component
export function InlineCode({ children }: { children: ReactNode }) {
  return (
    <code className="px-1.5 py-0.5 rounded-md bg-muted text-[13px] font-mono text-foreground border border-border/50">
      {children}
    </code>
  )
}

// Table Components
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="my-6 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        {children}
      </table>
    </div>
  )
}

export function TableHead({ children }: { children: ReactNode }) {
  return (
    <thead className="bg-muted/70 border-b border-border">
      {children}
    </thead>
  )
}

export function TableBody({ children }: { children: ReactNode }) {
  return (
    <tbody className="divide-y divide-border">
      {children}
    </tbody>
  )
}

export function TableRow({ children }: { children: ReactNode }) {
  return (
    <tr className="hover:bg-muted/30 transition-colors">
      {children}
    </tr>
  )
}

export function TableHeader({ children }: { children: ReactNode }) {
  return (
    <th className="px-4 py-3 text-left font-semibold text-foreground whitespace-nowrap">
      {children}
    </th>
  )
}

export function TableCell({ children }: { children: ReactNode }) {
  return (
    <td className="px-4 py-3 text-foreground/80">
      {children}
    </td>
  )
}

// Heading Components with anchor links
interface HeadingProps {
  children: ReactNode
  level: 1 | 2 | 3
}

export function Heading({ children, level }: HeadingProps) {
  const text = String(children)
  const id = slugify(text)

  const baseClasses = "group relative scroll-mt-24 flex items-center gap-2"

  const levelClasses = {
    1: "text-2xl font-bold tracking-tight mt-10 mb-6",
    2: "text-xl font-semibold tracking-tight mt-12 mb-4 pb-3 border-b border-border",
    3: "text-lg font-semibold mt-8 mb-3",
  }

  const content = (
    <>
      <span>{children}</span>
      <a
        href={`#${id}`}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
        aria-label={`Link to ${text}`}
      >
        <Link className="h-4 w-4" />
      </a>
    </>
  )

  if (level === 1) {
    return <h1 id={id} className={cn(baseClasses, levelClasses[1])}>{content}</h1>
  }
  if (level === 2) {
    return <h2 id={id} className={cn(baseClasses, levelClasses[2])}>{content}</h2>
  }
  return <h3 id={id} className={cn(baseClasses, levelClasses[3])}>{content}</h3>
}

// Blockquote Component
export function Blockquote({ children }: { children: ReactNode }) {
  return (
    <blockquote className="my-6 border-l-4 border-primary bg-muted/50 rounded-r-lg py-4 px-5 text-foreground/80 [&>p]:my-0">
      {children}
    </blockquote>
  )
}

// List Components
export function UnorderedList({ children }: { children: ReactNode }) {
  return (
    <ul className="my-4 ml-6 list-disc space-y-2 text-foreground/80 [&>li]:pl-1.5 marker:text-muted-foreground">
      {children}
    </ul>
  )
}

export function OrderedList({ children }: { children: ReactNode }) {
  return (
    <ol className="my-4 ml-6 list-decimal space-y-2 text-foreground/80 [&>li]:pl-1.5 marker:text-muted-foreground marker:font-medium">
      {children}
    </ol>
  )
}

export function ListItem({ children }: { children: ReactNode }) {
  return (
    <li className="leading-7">
      {children}
    </li>
  )
}

// Paragraph Component
export function Paragraph({ children }: { children: ReactNode }) {
  return (
    <p className="my-4 leading-7 text-foreground/80">
      {children}
    </p>
  )
}

// Link Component
export function Anchor({ children, href }: { children: ReactNode; href?: string }) {
  const isExternal = href?.startsWith('http')

  return (
    <a
      href={href}
      className="text-primary font-medium hover:underline underline-offset-4"
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  )
}

// Horizontal Rule
export function HorizontalRule() {
  return <hr className="my-8 border-border" />
}

// Strong/Bold
export function Strong({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-foreground">{children}</strong>
}

// Emphasis/Italic
export function Emphasis({ children }: { children: ReactNode }) {
  return <em className="italic">{children}</em>
}
